import React, { useEffect, useState } from 'react';

import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const supportedLngs = ['en', 'fr', 'ar'];

function getValidLang(i18nInstance: { resolvedLanguage?: string; language?: string }): string {
  const raw = (i18nInstance.resolvedLanguage || i18nInstance.language || 'en').split('-')[0].toLowerCase();
  return supportedLngs.includes(raw) ? raw : 'en';
}

function LanguageSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  
  useEffect(() => {
    const currentLang = getValidLang(i18n);
    if (lang && supportedLngs.includes(lang) && currentLang !== lang) {
      i18n.changeLanguage(lang);
    }
    
    // Set RTL direction for Arabic, LTR for others
    if (lang && supportedLngs.includes(lang)) {
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  if (lang && !supportedLngs.includes(lang)) {
    const fallback = getValidLang(i18n);
    return <Navigate to={`/${fallback}${location.pathname}${location.search}${location.hash}`} replace />;
  }

  return <Outlet />;
}

function RootRedirect() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = getValidLang(i18n);
  const cleanPath = location.pathname === '/' ? '' : location.pathname;
  return <Navigate to={`/${lang}${cleanPath}${location.search}${location.hash}`} replace />;
}

import Layout from './components/layout/Layout';
import ScrollToTop from './components/layout/ScrollToTop';
import CustomCursor from './components/CustomCursor';
import SmoothScroll from './components/SmoothScroll';
import Home from './pages/Home';
const About = React.lazy(() => import('./pages/About'));
const Works = React.lazy(() => import('./pages/Works'));
const Services = React.lazy(() => import('./pages/Services'));
const Projects = React.lazy(() => import('./pages/Projects'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Contact = React.lazy(() => import('./pages/Contact'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const WorkDetail = React.lazy(() => import('./pages/WorkDetail'));
const Catalogue = React.lazy(() => import('./pages/Catalogue'));
const CategoryDetail = React.lazy(() => import('./pages/CategoryDetail'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Maintenance = React.lazy(() => import('./pages/Maintenance'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const LegalNotices = React.lazy(() => import('./pages/LegalNotices'));
const MaisonShanghai = React.lazy(() => import('./pages/MaisonShanghai'));
const Exhibitions = React.lazy(() => import('./pages/Exhibitions'));

function AppContent() {
  const [isVip, setIsVip] = useState(localStorage.getItem('vip_access') === 'true');
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('vip') === 'true') {
      localStorage.setItem('vip_access', 'true');
      setIsVip(true);
    }
  }, [location]);

  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  if (isMaintenanceMode && !isVip) {
    return (
      <React.Suspense fallback={null}>
        <Maintenance />
      </React.Suspense>
    );
  }

  return (
    <>
      <CustomCursor />
      <ScrollToTop />
      <Routes>
        <Route path="/:lang" element={<LanguageSync />}>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="works" element={<Works />} />
            <Route path="works/:id" element={<WorkDetail />} />
            <Route path="catalogue" element={<Catalogue />} />
            <Route path="catalogue/:id" element={<CategoryDetail />} />
            <Route path="services" element={<Services />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="contact" element={<Contact />} />
            <Route path="faq" element={<FAQ />} />
            <Route path="exhibitions" element={<Exhibitions />} />
            <Route path="events" element={<Navigate to="../exhibitions" replace />} />
            <Route path="maison-shanghai-2026" element={<MaisonShanghai />} />
            <Route path="legal" element={<LegalNotices />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SmoothScroll>
        <AppContent />
      </SmoothScroll>
    </BrowserRouter>
  );
}
